import React, { useState, useMemo } from "react";
import "../styles/PerformanceMetrics.css";

const PerformanceMetrics = ({ tasks, drivers, isOpen, onClose }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [selectedDriver, setSelectedDriver] = useState('all');

  // Calculate performance metrics
  const metrics = useMemo(() => {
    try {
      console.log('PerformanceMetrics: Calculating metrics...', { selectedPeriod, selectedDriver, tasks, drivers });
      
      const now = new Date();
      let startDate = new Date();
      
      // Set start date based on selected period
      switch (selectedPeriod) {
        case 'today':
          startDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(now.getMonth() - 1);
          break;
      }

      // Filter tasks by period with safe access
      const allTasks = Object.values(tasks || {}).flat().filter(Boolean);
      console.log('PerformanceMetrics: All tasks count:', allTasks.length);
      
      const periodTasks = allTasks.filter(task => {
        if (!task || !task.created_at) return false;
        try {
          const taskDate = new Date(task.created_at);
          return taskDate >= startDate;
        } catch (error) {
          console.warn('PerformanceMetrics: Invalid date in task:', task.created_at);
          return false;
        }
      });

      // Filter by driver if selected
      const filteredTasks = selectedDriver === 'all' 
        ? periodTasks 
        : periodTasks.filter(task => task && task.livreur_affecte === selectedDriver);

      console.log('PerformanceMetrics: Filtered tasks count:', filteredTasks.length);

      // Calculate metrics with safe access
      const totalOrders = filteredTasks.length;
      const completedOrders = filteredTasks.filter(task => 
        task && task.status_delivery === 'SUCCESS'
      ).length;
      
      const failedOrders = filteredTasks.filter(task => 
        task && task.status_delivery === 'FAILED'
      ).length;

      const successRate = totalOrders > 0 ? (completedOrders / totalOrders) * 100 : 0;

      // Calculate average delivery time with better validation
      const completedWithTimes = filteredTasks.filter(task => 
        task && task.status_delivery === 'SUCCESS' && task.created_at && task.updated_at
      );
      
      let avgDeliveryTime = 0;
      if (completedWithTimes.length > 0) {
        const totalTime = completedWithTimes.reduce((sum, task) => {
          try {
            const created = new Date(task.created_at);
            const updated = new Date(task.updated_at);
            if (isNaN(created.getTime()) || isNaN(updated.getTime())) return sum;
            const diffMinutes = (updated - created) / (1000 * 60);
            return sum + (diffMinutes > 0 ? diffMinutes : 0);
          } catch (error) {
            console.warn('PerformanceMetrics: Error calculating delivery time:', error);
            return sum;
          }
        }, 0);
        avgDeliveryTime = totalTime / completedWithTimes.length;
      }

      // Calculate revenue with safe access
      const totalRevenue = filteredTasks.reduce((sum, task) => {
        const price = parseFloat(task?.prix_total) || 0;
        if (isNaN(price)) return sum;
        return sum + price;
      }, 0);

      // Calculate driver utilization with safe access
      const allDriversList = Object.values(drivers || {}).flat().filter(Boolean);
      const totalDrivers = allDriversList.length;
      const activeDrivers = (drivers?.busy?.length || 0) + (drivers?.free?.length || 0);
      const utilizationRate = totalDrivers > 0 ? (activeDrivers / totalDrivers) * 100 : 0;

      // Calculate average orders per driver with safe access
      const driverOrders = {};
      filteredTasks.forEach(task => {
        if (task && task.livreur_affecte && task.livreur_affecte !== 'Not Assigned') {
          driverOrders[task.livreur_affecte] = (driverOrders[task.livreur_affecte] || 0) + 1;
        }
      });

      const avgOrdersPerDriver = Object.keys(driverOrders).length > 0
        ? Object.values(driverOrders).reduce((sum, count) => sum + count, 0) / Object.keys(driverOrders).length
        : 0;

      // Top performing drivers
      const topDrivers = Object.entries(driverOrders)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([name, count]) => ({ name, count }));

      // Calculate peak hours
      const hourStats = {};
      filteredTasks.forEach(task => {
        if (task && task.created_at) {
          try {
            const hour = new Date(task.created_at).getHours();
            hourStats[hour] = (hourStats[hour] || 0) + 1;
          } catch (error) {
            console.warn('PerformanceMetrics: Error parsing hour:', error);
          }
        }
      });

      const peakHour = Object.entries(hourStats)
        .sort(([,a], [,b]) => b - a)[0];

      const result = {
        totalOrders,
        completedOrders,
        failedOrders,
        successRate,
        avgDeliveryTime,
        totalRevenue,
        utilizationRate,
        avgOrdersPerDriver,
        topDrivers,
        peakHour: peakHour ? `${peakHour[0].toString().padStart(2, '0')}:00` : 'N/A'
      };
      
      console.log('PerformanceMetrics: Calculated metrics:', result);
      return result;
      
    } catch (error) {
      console.error('PerformanceMetrics: Error calculating metrics:', error);
      return {
        totalOrders: 0,
        completedOrders: 0,
        failedOrders: 0,
        successRate: 0,
        avgDeliveryTime: 0,
        totalRevenue: 0,
        utilizationRate: 0,
        avgOrdersPerDriver: 0,
        topDrivers: [],
        peakHour: 'N/A'
      };
    }
  }, [tasks, drivers, selectedPeriod, selectedDriver]);

  // Get all unique drivers with safe access
  const allDrivers = useMemo(() => {
    const drivers = new Set();
    Object.values(tasks || {}).flat().forEach(task => {
      if (task && task.livreur_affecte && task.livreur_affecte !== 'Not Assigned') {
        drivers.add(task.livreur_affecte);
      }
    });
    return Array.from(drivers).sort();
  }, [tasks]);

  // Generate chart data based on real data and selected period/driver
  const generateChartData = useMemo(() => {
    try {
      console.log('PerformanceMetrics: Generating chart data...', { selectedPeriod, selectedDriver });
      
      const now = new Date();
      let startDate = new Date();
      
      // Set start date based on selected period
      switch (selectedPeriod) {
        case 'today':
          startDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(now.getMonth() - 1);
          break;
      }

      // Get filtered tasks (same logic as metrics)
      const allTasks = Object.values(tasks || {}).flat().filter(Boolean);
      const periodTasks = allTasks.filter(task => {
        if (!task || !task.created_at) return false;
        try {
          const taskDate = new Date(task.created_at);
          return taskDate >= startDate;
        } catch (error) {
          return false;
        }
      });

      const filteredTasks = selectedDriver === 'all' 
        ? periodTasks 
        : periodTasks.filter(task => task && task.livreur_affecte === selectedDriver);

      if (selectedPeriod === 'today') {
        // 24-hour format for today
        const hourStats = {};
        for (let i = 0; i < 24; i++) {
          hourStats[i] = 0;
        }
        
        filteredTasks.forEach(task => {
          if (task && task.created_at) {
            try {
              const hour = new Date(task.created_at).getHours();
              if (hour >= 0 && hour <= 23) {
                hourStats[hour] = (hourStats[hour] || 0) + 1;
              }
            } catch (error) {
              console.warn('PerformanceMetrics: Error parsing hour for chart:', error);
            }
          }
        });

        // Create data for every 3 hours (8 data points)
        const chartData = [];
        const timeSlots = [6, 9, 12, 15, 18, 21]; // 6AM, 9AM, 12PM, 3PM, 6PM, 9PM
        
        timeSlots.forEach(hour => {
          const label = hour.toString().padStart(2, '0') + ':00';
          const value = hourStats[hour] || 0;
          chartData.push({
            label,
            value,
            unit: '',
            color: '#2ed573'
          });
        });

        console.log('PerformanceMetrics: Today chart data:', chartData);
        return chartData;
        
      } else if (selectedPeriod === 'week') {
        // Daily data for week
        const dayStats = {};
        const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
        
        for (let i = 0; i < 7; i++) {
          dayStats[i] = 0;
        }
        
        filteredTasks.forEach(task => {
          if (task && task.created_at) {
            try {
              const taskDate = new Date(task.created_at);
              const dayOfWeek = taskDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
              // Convert to Monday = 0 format
              const adjustedDay = (dayOfWeek + 6) % 7;
              dayStats[adjustedDay] = (dayStats[adjustedDay] || 0) + 1;
            } catch (error) {
              console.warn('PerformanceMetrics: Error parsing day for chart:', error);
            }
          }
        });

        const chartData = dayNames.map((day, index) => ({
          label: day,
          value: dayStats[index] || 0,
          unit: '',
          color: '#2ed573'
        }));

        console.log('PerformanceMetrics: Week chart data:', chartData);
        return chartData;
        
      } else if (selectedPeriod === 'month') {
        // Weekly data for month
        const weekStats = {};
        
        filteredTasks.forEach(task => {
          if (task && task.created_at) {
            try {
              const taskDate = new Date(task.created_at);
              const weekOfMonth = Math.ceil(taskDate.getDate() / 7);
              const weekKey = Math.min(weekOfMonth, 4); // Max 4 weeks
              weekStats[weekKey] = (weekStats[weekKey] || 0) + 1;
            } catch (error) {
              console.warn('PerformanceMetrics: Error parsing week for chart:', error);
            }
          }
        });

        const chartData = [
          { label: 'Sem 1', value: weekStats[1] || 0, unit: '', color: '#2ed573' },
          { label: 'Sem 2', value: weekStats[2] || 0, unit: '', color: '#2ed573' },
          { label: 'Sem 3', value: weekStats[3] || 0, unit: '', color: '#2ed573' },
          { label: 'Sem 4', value: weekStats[4] || 0, unit: '', color: '#2ed573' }
        ];

        console.log('PerformanceMetrics: Month chart data:', chartData);
        return chartData;
      }

      return [];
      
    } catch (error) {
      console.error('PerformanceMetrics: Error generating chart data:', error);
      return [];
    }
  }, [tasks, selectedPeriod, selectedDriver]);

  if (!isOpen) return null;

  return (
    <div className="performance-metrics-modal">
      <div className="performance-metrics-content">
        <div className="metrics-header">
          <h3>
            <i className="fas fa-chart-line"></i>
            Métriques de performance
          </h3>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="metrics-body">
          {/* Filters */}
          <div className="metrics-filters">
            <div className="filter-group">
              <label>Période :</label>
              <select 
                value={selectedPeriod} 
                onChange={(e) => setSelectedPeriod(e.target.value)}
              >
                <option value="today">Aujourd'hui</option>
                <option value="week">7 derniers jours</option>
                <option value="month">Dernier mois</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label>Livreur :</label>
              <select 
                value={selectedDriver} 
                onChange={(e) => setSelectedDriver(e.target.value)}
              >
                <option value="all">Tous les livreurs</option>
                {allDrivers.map(driver => (
                  <option key={driver} value={driver}>{driver}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="key-metrics-grid">
            <div className="metric-card success">
              <div className="metric-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <div className="metric-info">
                <div className="metric-value">{metrics.totalOrders}</div>
                <div className="metric-label">Total des commandes</div>
              </div>
            </div>

            <div className="metric-card success">
              <div className="metric-icon">
                <i className="fas fa-trophy"></i>
              </div>
              <div className="metric-info">
                <div className="metric-value">{metrics.completedOrders}</div>
                <div className="metric-label">Terminées</div>
              </div>
            </div>

            <div className="metric-card warning">
              <div className="metric-icon">
                <i className="fas fa-percentage"></i>
              </div>
              <div className="metric-info">
                <div className="metric-value">{metrics.successRate.toFixed(1)}%</div>
                <div className="metric-label">Taux de réussite</div>
              </div>
            </div>

            <div className="metric-card info">
              <div className="metric-icon">
                <i className="fas fa-clock"></i>
              </div>
              <div className="metric-info">
                <div className="metric-value">{Math.round(metrics.avgDeliveryTime)}m</div>
                <div className="metric-label">Temps de livraison moyen</div>
              </div>
            </div>

            <div className="metric-card revenue">
              <div className="metric-icon">
                <i className="fas fa-money-bill-wave"></i>
              </div>
              <div className="metric-info">
                <div className="metric-value">{metrics.totalRevenue.toFixed(0)} MAD</div>
                <div className="metric-label">Revenus totaux</div>
              </div>
            </div>

            <div className="metric-card utilization">
              <div className="metric-icon">
                <i className="fas fa-users"></i>
              </div>
              <div className="metric-info">
                <div className="metric-value">{metrics.utilizationRate.toFixed(1)}%</div>
                <div className="metric-label">Utilisation des livreurs</div>
              </div>
            </div>
          </div>

          {/* Additional Stats */}
          <div className="additional-stats">
            <div className="stats-section">
              <h4>Performance des livreurs</h4>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Moyenne des commandes par livreur :</span>
                  <span className="stat-value">{metrics.avgOrdersPerDriver.toFixed(1)}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Commandes échouées :</span>
                  <span className="stat-value error">{metrics.failedOrders || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Heure de pointe :</span>
                  <span className="stat-value">{metrics.peakHour}</span>
                </div>
              </div>
            </div>

            <div className="stats-section">
              <h4>Meilleurs livreurs</h4>
              <div className="top-drivers-list">
                {metrics.topDrivers.length > 0 ? (
                  metrics.topDrivers.map((driver, index) => (
                    <div key={driver.name} className="driver-rank">
                      <div className="rank-number">{index + 1}</div>
                      <div className="driver-name">{driver.name}</div>
                      <div className="driver-orders">{driver.count} commandes</div>
                    </div>
                  ))
                ) : (
                  <p className="no-data">Aucune donnée de livreur disponible</p>
                )}
              </div>
            </div>
          </div>

          {/* Performance Trend Chart */}
          <div className="chart-section">
            <h4>Tendance de performance - {selectedPeriod === 'today' ? 'Aujourd\'hui' : selectedPeriod === 'week' ? '7 derniers jours' : 'Dernier mois'}</h4>
            <div className="performance-chart">
              <div className="chart-container">
                <div className="chart-bars">
                  {generateChartData.length > 0 ? generateChartData.map((data, index) => {
                    const maxValue = Math.max(...generateChartData.map(d => d.value));
                    const heightPercentage = maxValue > 0 ? Math.max((data.value / maxValue) * 100, 8) : 8;
                    
                    return (
                      <div key={index} className="chart-bar-container">
                        <div 
                          className="chart-bar"
                          style={{
                            height: `${heightPercentage}%`,
                            backgroundColor: data.color,
                            minHeight: '20px'
                          }}
                          title={`${data.label}: ${data.value} commandes`}
                        >
                          <span className="bar-value">{data.value}</span>
                        </div>
                        <div className="bar-label">{data.label}</div>
                      </div>
                    );
                  }) : (
                    <div className="no-chart-data">
                      <i className="fas fa-chart-bar"></i>
                      <p>Aucune donnée disponible pour cette période</p>
                    </div>
                  )}
                </div>
                <div className="chart-info">
                  <div className="chart-summary">
                    <span>Total: {generateChartData.reduce((sum, data) => sum + data.value, 0)} commandes</span>
                    <span>Pic: {generateChartData.length > 0 ? Math.max(...generateChartData.map(d => d.value)) : 0} commandes</span>
                  </div>
                </div>
                <div className="chart-legend">
                  <div className="legend-item">
                    <span className="legend-color" style={{backgroundColor: '#2ed573'}}></span>
                    <span>Nombre de commandes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMetrics;
